import { motion } from "framer-motion";
import { MessageCircle, ArrowDown, MapPin, Phone, Sparkles, ShieldCheck, HeartHandshake, BadgeCheck } from "lucide-react";
import { LogoMark } from "./Logo";
import {
  waLink,
  DEFAULT_WA_MESSAGE,
  PHONE_DISPLAY,
  PHONE_TEL,
  SERVICE_AREA,
} from "../data/content";

const BUBBLES = [
  { size: 130, top: "12%", left: "6%", delay: "0s" },
  { size: 70, top: "60%", left: "12%", delay: "1.6s" },
  { size: 190, top: "20%", right: "4%", delay: "0.8s" },
  { size: 50, top: "72%", right: "16%", delay: "2.4s" },
];

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="hero-glow relative overflow-hidden pt-28 sm:pt-36 pb-20 sm:pb-28"
    >
      {BUBBLES.map((b, i) => (
        <div
          key={i}
          className="bubble absolute rounded-full border border-sky-400/15 bg-sky-400/5 pointer-events-none"
          style={{
            width: b.size,
            height: b.size,
            top: b.top,
            left: b.left,
            right: b.right,
            animationDelay: b.delay,
          }}
        />
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-1.5 text-xs sm:text-sm font-semibold text-sky-300 mb-6"
            >
              <MapPin className="h-3.5 w-3.5" />
              Limpeza profissional no {SERVICE_AREA}
            </div>

            <h1
              data-testid="hero-heading"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]"
            >
              Limpeza profissional que{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-300">
                renova tudo
              </span>
            </h1>

            <p
              data-testid="hero-subtext"
              className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
            >
              Higienização profunda de sofás, colchões, carpetes, telhados e
              terraços. Devolvemos o aspeto de novo aos seus estofados e
              superfícies, ao domicílio, em todo o {SERVICE_AREA}.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1eb857] text-[#06281a] font-bold text-base px-8 py-4 rounded-full transition-all shadow-xl shadow-[#25D366]/30 hover:-translate-y-0.5"
              >
                <MessageCircle className="h-5 w-5" />
                Pedir Orçamento Grátis
              </a>
              <a
                href="#precos"
                data-testid="hero-prices-button"
                className="inline-flex items-center justify-center gap-2.5 border border-sky-400/40 bg-sky-400/10 hover:bg-sky-400/20 text-sky-200 font-semibold text-base px-8 py-4 rounded-full transition-colors"
              >
                Ver Tabela de Preços
                <ArrowDown className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-400">
              <a
                href={PHONE_TEL}
                data-testid="hero-phone-link"
                className="inline-flex items-center gap-2 hover:text-sky-300 transition-colors"
              >
                <Phone className="h-4 w-4 text-sky-400" />
                {PHONE_DISPLAY}
              </a>
              <span
                data-testid="hero-trust-line"
                className="inline-flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4 text-cyan-300" />
                Qualidade • Confiança • Resultados
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
            className="relative mx-auto w-full max-w-md"
          >
            <div className="absolute -inset-8 bg-sky-500/20 blur-3xl rounded-full pointer-events-none" />
            <div
              data-testid="hero-logo-card"
              className="relative rounded-[2rem] border border-sky-400/25 bg-gradient-to-b from-[#162550]/90 to-[#0B1430]/90 backdrop-blur-xl p-10 sm:p-12 shadow-2xl shadow-sky-500/15 rotate-1 hover:rotate-0 transition-transform duration-500 text-center"
            >
              <div className="flex justify-center">
                <LogoMark size="lg" />
              </div>
              <p className="mt-7 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                EFEITO <span className="text-sky-400">NOVO</span>
              </p>
              <div className="mt-3 flex items-center justify-center gap-3">
                <span className="h-px w-8 bg-sky-400/50" />
                <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.25em] uppercase text-slate-400">
                  Limpeza profissional que renova tudo
                </span>
                <span className="h-px w-8 bg-sky-400/50" />
              </div>
              <div className="mt-7 flex items-center justify-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold text-slate-300">
                  <ShieldCheck className="h-3.5 w-3.5 text-sky-400" /> Qualidade
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold text-slate-300">
                  <HeartHandshake className="h-3.5 w-3.5 text-sky-400" /> Confiança
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold text-slate-300">
                  <BadgeCheck className="h-3.5 w-3.5 text-sky-400" /> Resultados
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
